import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import schema from '../data/sharedRecipeSchema.json';

/**
 * How an assistant hands a recipe to Snapcipe (#1066).
 *
 * Claude, ChatGPT or anything that can write a recipe and call a URL sends
 * it to the worker in plain words and gets a link back; the person opens
 * the link and the recipe is in their cookbook. The schema shown here is
 * the worker's own, copied by `node scripts/sync-shared-recipe-schema.mjs` and checked by the
 * worker's tests, so the page cannot describe a shape the worker refuses.
 */
const MINT_URL = 'https://prod-mobile-ai-recipe-app.s4tdrctc6w.workers.dev/share/mint';

const EXAMPLE_REQUEST = {
  recipe: {
    name: 'Red lentil dal',
    description: 'Weeknight dal, mild.',
    servings: 4,
    totalTime: '35 min',
    cuisine: 'Indian',
    ingredients: [
      '200 g red lentils',
      { name: 'coconut milk', quantity: 400, unit: 'ml' },
      { name: 'cumin', quantity: '1 tsp' },
      'salt',
    ],
    steps: ['Rinse the lentils.', 'Simmer with the coconut milk for 20 minutes.', 'Season.'],
  },
};

const EXAMPLE_RESPONSE = {
  id: 'k7Qm2xH9vN4p',
  url: 'https://snapcipe.app/r?s=k7Qm2xH9vN4p',
  expiresAt: '2026-12-04T10:00:00.000Z',
};

const INSTRUCTION = `When I ask you to send a recipe to Snapcipe, POST it as JSON to
${MINT_URL}
in the shape { "recipe": { name, description, servings, totalTime, prepTime,
cookTime, cuisine, ingredients, steps } } - ingredients as strings or
{ name, quantity, unit }, steps as plain sentences in order. Reply with the
"url" from the response and nothing else. Do not send a recipe I have not
asked you to send.`;

const AssistantsPage = () => (
  <div className="terms-page">
    <div className="container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="page-header">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Snapcipe
          </Link>
          <h1>Recipes from your assistant</h1>
          <p className="last-updated">
            Ask Claude or ChatGPT for a recipe, then ask for it as a Snapcipe link.
          </p>
        </div>

        <div className="terms-content">
          <section className="terms-section">
            <p>
              Your assistant writes the recipe. Snapcipe keeps it, scales it, cooks it with you and
              puts what is missing on your shopping list. The hand-over is one link: the assistant
              sends the recipe to Snapcipe in plain words and gets back an address such as{' '}
              <code>https://snapcipe.app/r?s=k7Qm2xH9vN4p</code>. Open it on your phone and the
              recipe is in your cookbook. Without the app, the same link shows the recipe in your
              browser.
            </p>
            <p>
              No account is needed, nothing is sent to a model by us, and nothing about the sender
              is stored. A link holds the recipe&apos;s words for thirty days, then it is gone.
            </p>
          </section>

          <section className="terms-section">
            <h2>Tell your assistant once</h2>
            <p>
              Paste this into your assistant&apos;s custom instructions, memory or project settings.
              After that, &ldquo;send this to Snapcipe&rdquo; is enough.
            </p>
            <pre className="code-block">
              <code>{INSTRUCTION}</code>
            </pre>
          </section>

          <section className="terms-section">
            <h2>The request</h2>
            <p>
              <code>POST {MINT_URL}</code> with a JSON body. A name plus at least one ingredient or
              one step; everything else is optional. Unknown keys are ignored. Up to ten links a
              minute per address.
            </p>
            <pre className="code-block">
              <code>{JSON.stringify(EXAMPLE_REQUEST, null, 2)}</code>
            </pre>
            <p>The answer:</p>
            <pre className="code-block">
              <code>{JSON.stringify(EXAMPLE_RESPONSE, null, 2)}</code>
            </pre>
            <p>
              A recipe that is not one comes back as <code>400</code> with a <code>message</code>{' '}
              that says what is missing. The eleventh link in a minute is <code>429</code>.
            </p>
          </section>

          <section className="terms-section">
            <h2>The schema</h2>
            <p>
              JSON Schema, the worker&apos;s own copy. Long fields are shortened to these limits,
              not refused.
            </p>
            <pre className="code-block">
              <code>{JSON.stringify(schema, null, 2)}</code>
            </pre>
          </section>

          <section className="terms-section">
            <h2>What it is not</h2>
            <p>
              The link is one way: your assistant cannot read your cookbook, your pantry or your
              list through it. That is a connector, and it is coming - signed in, with your
              permission, and with the same rule: the assistant generates, Snapcipe keeps.
            </p>
            <div className="contact-info">
              <p>
                <strong>Questions:</strong> support@snapcipe.app
              </p>
            </div>
          </section>
        </div>
      </motion.div>
    </div>
  </div>
);

export default AssistantsPage;
